import { LitElement, html } from "lit";

import { customElement, property, query, state } from "lit/decorators.js";

import { autoUpdate, computePosition, flip, shift } from "@floating-ui/dom";
import type { Placement } from "@floating-ui/dom";

import { dropdownMenuStyles } from "./dropdown-menu.css";

@customElement("dropdown-menu")
export class DropdownMenu extends LitElement {
  static override styles = [dropdownMenuStyles];

  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) glass = false;
  @property({ type: String }) placement: Placement = "bottom-start";

  @state() private hasSlottedItems = false;
  @state() private isOpen = false;

  @query(".dropdown-trigger") private triggerElement!: HTMLElement;
  @query(".dropdown-content") private dropdownElement!: HTMLElement;

  private autoUpdateCleanup?: () => void;
  private clickOutsideHandler?: (e: MouseEvent) => void;

  private setupFloatingUI() {
    if (!this.triggerElement || !this.dropdownElement) return;

    this.autoUpdateCleanup = autoUpdate(this.triggerElement, this.dropdownElement, () => {
      computePosition(this.triggerElement, this.dropdownElement, {
        placement: this.placement,
        strategy: "fixed",
        middleware: [flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        Object.assign(this.dropdownElement.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });
  }

  private cleanupFloatingUI() {
    this.autoUpdateCleanup?.();
    this.autoUpdateCleanup = undefined;
  }

  private async open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setAttribute("open", "true");
    await this.updateComplete;
    this.setupFloatingUI();
    this.setupClickOutside();
  }

  private close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.setAttribute("open", "false");
    this.cleanupFloatingUI();
    this.cleanupClickOutside();
  }

  private setupClickOutside() {
    this.clickOutsideHandler = (e: MouseEvent) => {
      const path = e.composedPath();
      if (!path.includes(this)) {
        this.close();
      }
    };
    setTimeout(() => {
      document.addEventListener("click", this.clickOutsideHandler!, { capture: true });
    }, 0);
  }

  private cleanupClickOutside() {
    if (this.clickOutsideHandler) {
      document.removeEventListener("click", this.clickOutsideHandler, { capture: true });
      this.clickOutsideHandler = undefined;
    }
  }

  private handleTriggerClick = (e: Event) => {
    e.stopPropagation();
    if (this.disabled) return;

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  private handleDropdownItemClick = (e: Event) => {
    e.stopPropagation();
    this.close();

    this.dispatchEvent(
      new CustomEvent("dropdown-item-click", {
        detail: (e as CustomEvent).detail,
        bubbles: true,
        composed: true,
      }),
    );
  };

  private handleSlotChange = () => {
    const slot = this.shadowRoot?.querySelector("slot:not([name])") as HTMLSlotElement;
    const assignedElements = slot?.assignedElements() || [];
    this.hasSlottedItems = assignedElements.length > 0;
  };

  protected override render() {
    return html`
      <div class="dropdown-container" @dropdown-item-click=${this.handleDropdownItemClick}>
        <button
          class="dropdown-trigger"
          type="button"
          ?disabled=${this.disabled}
          @click=${this.handleTriggerClick}
          aria-haspopup="menu"
          aria-expanded=${this.isOpen ? "true" : "false"}
        >
          <slot name="trigger"></slot>
        </button>

        <div class="dropdown-content" role="menu" ?hidden=${!this.isOpen}>
          <slot @slotchange=${this.handleSlotChange}></slot>
        </div>
      </div>
    `;
  }

  protected override firstUpdated() {
    this.handleSlotChange();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupFloatingUI();
    this.cleanupClickOutside();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dropdown-menu": DropdownMenu;
  }
}
